/**
 * [2026-11-03] Replacement cycle — the hardware view for tenants that sell kit in batches
 * (bibliotheca: library self-service; niryo: robotics training; positivo, jpik, verypc: IT Hardware
 * device fleets).
 *
 * Kit outlives its contract: 95-97% of these closed deals read Expired while the equipment is still
 * installed, so an expiry-led table parks the real targets (old installs) at the bottom. For these
 * tenants Contracts Intelligence and the Displacement Radar default to equipment AGE instead; every
 * other tenant is unchanged.
 *
 * The gate is the tenant's PRIMARY category (the first in its default list), not the row, so a
 * tenant whose main lens is software never sees this view even if it holds hardware deals.
 * Display only: the SAM trigger (api marketIntel.ts) is not affected.
 *
 * Data comes from the API's precomputed store (GET /api/procurement/replacement-cycle/page and
 * /radar), filtered, sorted and paged server-side — IT Hardware alone is 77k deals.
 */
import { getDealCategoriesForTenant } from '@/lib/procurementCompetitors';

const CATEGORY_KEY = {
  'Library Self-Service, RFID & Automated Handling': 'library_self_service',
  library_self_service: 'library_self_service',
  'Robotics, Automation & Industry 4.0 Training': 'robotics_automation_training',
  robotics_automation_training: 'robotics_automation_training',
  'IT Hardware (Education only)': 'it_hardware',
  'IT Hardware': 'it_hardware',
  it_hardware: 'it_hardware',
};
/** The closed_deals label each key is stored under (what the API expects). */
export const HARDWARE_LABEL = {
  library_self_service: 'Library Self-Service, RFID & Automated Handling',
  robotics_automation_training: 'Robotics, Automation & Industry 4.0 Training',
  it_hardware: 'IT Hardware (Education only)',
};

export function hardwareKey(c) {
  return CATEGORY_KEY[String(c || '').trim()] || null;
}
export function isHardwareCategory(c) {
  return hardwareKey(c) != null;
}

/** The tenant's primary category is hardware → replacement cycle is its default view. */
export function isHardwareTenant(tenantId) {
  if (!tenantId) return false;
  const [primary] = getDealCategoriesForTenant(tenantId);
  return isHardwareCategory(primary);
}

/** Band logic values, oldest first. Labels/colours are display only (same keys for every hardware category). */
export const REPLACEMENT_BANDS = ['overdue', 'due', 'plan', 'mid', 'new', 'undated'];
export const REPLACEMENT_BAND_LABEL_KEYS = {
  overdue: 'procRcBandOverdue',
  due: 'procRcBandDue',
  plan: 'procRcBandPlan',
  mid: 'procRcBandMid',
  new: 'procRcBandNew',
  undated: 'procRcBandUndated',
};
export function replacementBandColor(band) {
  switch (band) {
    case 'overdue': return 'bg-red-50 text-red-700 border-red-200';
    case 'due': return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'plan': return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'mid': return 'bg-teal-50 text-teal-700 border-teal-200';
    case 'new': return 'bg-gray-50 text-gray-600 border-gray-200';
    default: return 'bg-gray-100 text-gray-500 border-gray-200';
  }
}
/** Bands that mean "talk about replacement now". */
export const REPLACEMENT_WINDOW_BANDS = ['plan', 'due', 'overdue'];

/** Same cut-offs as the API (REPLACEMENT_BANDS_YEARS); the API also returns them as bands_years. */
export const BANDS_BY_KEY = {
  library_self_service: { mid: 3, plan: 5, due: 7, overdue: 12 },
  robotics_automation_training: { mid: 3, plan: 5, due: 7, overdue: 12 },
  // Laptops, Chromebooks and tablets refresh on a 3-5 year cycle.
  it_hardware: { mid: 2, plan: 3, due: 4, overdue: 6 },
};
export function bandsFor(categoryOrKey) {
  return BANDS_BY_KEY[hardwareKey(categoryOrKey) || categoryOrKey] || BANDS_BY_KEY.library_self_service;
}

/** "5–7 yrs", "12+ yrs", "under 3 yrs" for a band, from the category's cut-offs. */
export function bandRangeLabel(band, b, t) {
  switch (band) {
    case 'overdue': return t('procRcRangeOver', { a: b.overdue });
    case 'due': return t('procRcRangeYears', { a: b.due, b: b.overdue });
    case 'plan': return t('procRcRangeYears', { a: b.plan, b: b.due });
    case 'mid': return t('procRcRangeYears', { a: b.mid, b: b.plan });
    case 'new': return t('procRcRangeUnder', { a: b.mid });
    default: return t('procRcRangeUndated');
  }
}

export function bandForAge(age, categoryOrKey) {
  const b = bandsFor(categoryOrKey);
  if (age == null || Number.isNaN(age)) return 'undated';
  if (age >= b.overdue) return 'overdue';
  if (age >= b.due) return 'due';
  if (age >= b.plan) return 'plan';
  if (age >= b.mid) return 'mid';
  return 'new';
}
export function ageInYears(iso, now = new Date()) {
  const t = Date.parse(iso || '');
  return Number.isNaN(t) ? null : (now.getTime() - t) / (365.25 * 24 * 3600 * 1000);
}

/*
 * Purchase kind for ONE deal (the deal page). 🔴 Mirrors classifyPurchase in
 * api/src/services/replacementCycle.ts — change both together. System purchases date the kit;
 * service contracts keep their real expiry; consumables never date anything; 'other' (IT Hardware
 * that is not a device: servers, networking, printers, furniture) is not part of the fleet at all.
 */
const SERVICE_RE = /maint[ae]n|mantenim|manuten|wartung|servis|serwis|bak[ıi]m|onarım|support|soporte|repair|reparac|naprav|opravy|hooldus|karbantart|service (agreement|contract|plan)|servicio de|annual (service|licen)|subscription|suscrip|licen[cs]e renewal|renovaci[oó]n|extended warranty/i;
const CONSUMABLE_RE = /\btags?\b|etiquet|etiket|\blabels?\b|\bstrips?\b|bandas? magn|cintas?|consumab|consumibl|insumos|stickers?|tattle ?tape|activator|deactivator|spare parts|repuestos|n[aá]hradn|yedek par/i;
const SUPPLY_RE = /suppl(y|ies)|fourniture|suministro|adquisici|aquisi[cç]|compra|purchase of|procurement of|dod[aá]vk|n[aá]kup|zakup|dostaw|lieferung|beschaffung|tedarik|al[ıi]m[ıi]|beszerz|ostmine|install|instala/i;
const EQUIPMENT_RE = /equip|kiosk|gates?\b|\barch(es)?\b|portal|station|sorter|sorting|robot|\bcell\b|\barms?\b|cobot|machine|hardware|trainer|workstation|book ?drop|lockers?/i;
const IT_DEVICE_RE = /laptop|notebook|chromebook|tablet|ipad|macbook|port[aá]til|computador|desktop|\bpcs?\b|ordinat|comput|c[oó]mputo|po[cč][ií]ta[cč]|v[yý]po[cč]etn|informati|inform[aá]tic|sz[aá]m[ií]t[oó]g[eé]p|sz[aá]m[ií]t[aá]stechn|компютър|workstation|esta[cç][aã]o de trabalho|stații de lucru|interactive|interattiv|lousa|pizarra|dispositiv|device|apple|dell|lenovo|hp\b|acer|asus|positivo|jp\.?ik|verypc|\bict\b|hardware/i;
const IT_OTHER_RE = /server|servidor|storage|\bswitch|router|network|\brede\b|apparati di rete|firewall|data communications|\bcisco\b|printer|impres|stampant|toner|cartrid|\bups\b|cabl|wi-?fi|access point|furniture|m[oö][oö]bl|sillas|mobili|office supplies|spectrofot|laborator(y|io) (instrument|toestel)|software only|licen[cs]e only/i;
const IT_CLEAR_DEVICE_RE = /laptop|notebook|chromebook|tablet|desktop|\bpcs?\b|port[aá]til|computador/i;

export function purchaseKind(deal) {
  const text = `${deal?.contract_scope ?? ''} ${deal?.product_name ?? ''}`;
  const serviceOnly = String(deal?.service_only ?? deal?.services_only ?? '').trim().toLowerCase() === 'yes';
  if (hardwareKey(deal?.category) === 'it_hardware') {
    if (/toner|cartrid|ink\b|tinta/i.test(text)) return 'consumable';
    if (IT_OTHER_RE.test(text) && !IT_CLEAR_DEVICE_RE.test(text)) return 'other';
    if (text.trim() && !IT_DEVICE_RE.test(text)) return 'other';
    if (serviceOnly && !SUPPLY_RE.test(text)) return 'service';
    if (SERVICE_RE.test(text) && !SUPPLY_RE.test(text) && !IT_CLEAR_DEVICE_RE.test(text)) return 'service';
    return 'system';
  }
  if (CONSUMABLE_RE.test(text) && !EQUIPMENT_RE.test(text)) return 'consumable';
  if (SUPPLY_RE.test(text) && !/^\s*(maint|mantenim|manuten|wartung|servis|serwis)/i.test(text)) return 'system';
  if (serviceOnly) return 'service';
  if (SERVICE_RE.test(text)) return 'service';
  return 'system';
}
